
import { AccountInfo } from '@azure/msal-browser';
import logger from './logger';

// Check if group access control is enabled (only for non-production environments by default)
const ENABLE_GROUP_CHECK = process.env.REACT_APP_ENABLE_GROUP_CHECK === 'true';

const ALLOWED_GROUP_IDS = process.env.REACT_APP_ALLOWED_GROUP_IDS
  ? process.env.REACT_APP_ALLOWED_GROUP_IDS.split(',').map(id => id.trim())
  : [];

export interface GroupCheckResult {
  hasAccess: boolean;
  userGroups: string[];
  message?: string;
}

/**
 * Check if user belongs to allowed groups
 * @param account - MSAL account information containing group claims
 * @returns GroupCheckResult with access status
 */
export function checkUserGroupAccess(account: AccountInfo): GroupCheckResult {
  try {
    // If group check is disabled, allow all authenticated users
    if (!ENABLE_GROUP_CHECK) {
      logger.log('ℹ️ Group access check is disabled - allowing all authenticated users');
      return {
        hasAccess: true,
        userGroups: [],
        message: undefined
      };
    }

    // Groups can be found in idTokenClaims
    const claims = account.idTokenClaims as any;
    
    const userGroups: string[] = claims?.groups || [];
    const userRoles: string[] = claims?.roles || [];
    
    // If no allowed groups configured, deny access (fail-safe)
    if (ALLOWED_GROUP_IDS.length === 0) {
      console.warn('⚠️ Group check enabled but no allowed groups configured - denying access');
      return {
        hasAccess: false,
        userGroups,
        message: 'Access control is enabled but not properly configured. Please contact support.'
      };
    }
    
    // Check if user is in any of the allowed groups
    const hasAccess = ALLOWED_GROUP_IDS.some(allowedId => 
      userGroups.includes(allowedId) || userRoles.includes(allowedId)
    );
    
    if (!hasAccess) {
      console.warn('User does not belong to allowed groups', {
        userGroups,
        allowedGroups: ALLOWED_GROUP_IDS
      });
    } else {
      logger.log('✅ User authorized - belongs to allowed group');
    }
    
    return {
      hasAccess,
      userGroups,
      message: hasAccess 
        ? undefined 
        : 'Access denied. Your account is not authorized to use this application.'
    };
  } catch (error) {
    console.error('Error checking user groups:', error);
    return {
      hasAccess: false,
      userGroups: [],
      message: 'Error verifying access. Please try again or contact support.'
    };
  }
}
